import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import authService from '../services/authService';
import AdminLayout from '../components/AdminLayout';

interface DashboardStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  total_contacts: number;
  total_messages: number;
  messages_sent_today: number;
  messages_received_today: number;
  active_campaigns: number;
  completed_campaigns: number;
  scheduled_campaigns: number;
  total_templates: number;
  whatsapp_phones_connected: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      const response = await axios.get<DashboardStats>(
        `${API_BASE_URL}/admin/dashboard/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard statistics');
      console.error('Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow h-32"></div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">

        {/* Stats Grid */}
        {stats && (
          <>
            {/* User Stats */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">User Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                  title="Total Users"
                  value={stats.total_users}
                  icon="👥"
                  color="blue"
                />
                <StatCard
                  title="Active Users"
                  value={stats.active_users}
                  icon="✅"
                  color="green"
                />
                <StatCard
                  title="Inactive Users"
                  value={stats.inactive_users}
                  icon="⏸️"
                  color="gray"
                />
              </div>
            </div>

            {/* Message Stats */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Message Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                  title="Total Messages"
                  value={stats.total_messages}
                  icon="💬"
                  color="purple"
                />
                <StatCard
                  title="Sent Today"
                  value={stats.messages_sent_today}
                  icon="📤"
                  color="blue"
                />
                <StatCard
                  title="Received Today"
                  value={stats.messages_received_today}
                  icon="📥"
                  color="green"
                />
              </div>
            </div>

            {/* Campaign Stats */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Campaign Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                  title="Active Campaigns"
                  value={stats.active_campaigns}
                  icon="🚀"
                  color="green"
                />
                <StatCard
                  title="Completed"
                  value={stats.completed_campaigns}
                  icon="✔️"
                  color="blue"
                />
                <StatCard
                  title="Scheduled"
                  value={stats.scheduled_campaigns}
                  icon="📅"
                  color="orange"
                />
              </div>
            </div>

            {/* Other Stats */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Other Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                  title="Total Contacts"
                  value={stats.total_contacts}
                  icon="📇"
                  color="indigo"
                />
                <StatCard
                  title="Templates"
                  value={stats.total_templates}
                  icon="📝"
                  color="pink"
                />
                <StatCard
                  title="WhatsApp Phones"
                  value={stats.whatsapp_phones_connected}
                  icon="📱"
                  color="green"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'gray' | 'indigo' | 'pink';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    gray: 'bg-gray-50 text-gray-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    pink: 'bg-pink-50 text-pink-600',
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value.toLocaleString()}</p>
        </div>
        <div className={`text-4xl ${colorClasses[color]} p-3 rounded-full`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
